import {
  Box,
  CircularProgress,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { SearchIcon } from 'lucide-react';
import { ChangeEvent, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useFeatureFlagApi } from '../../services/api/useFeatureFlag.api';
import { ApiResponse } from '../../types/ApiResponse.type';
import { FeatureFlag } from '../../types/FeatureFlag/FeatureFlag.type';
import { FeatureFlagAccess } from '../../types/FeatureFlag/FeatureFlagUpdate.type';
import FeatureFlagRow from './FeatureFlagRow';

interface FeatureFlagsTableProps {
  snackbarData: (message: string, severity: 'success' | 'error') => void;
}

function FeatureFlagsTable({ snackbarData }: FeatureFlagsTableProps) {
  const queryClient = useQueryClient();
  const { getAllFeatureFlags, toggleFeatureFlag, updateFeatureFlagAccess } = useFeatureFlagApi();

  const [searchText, setSearchText] = useState('');
  const query = useDebouncedValue(searchText, 500);

  const { ref: flagsRef, inView: flagsInView } = useInView();

  const {
    data: infiniteFlags,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['manageFeatureFlags', query],
    queryFn: ({ pageParam = 1 }) =>
      getAllFeatureFlags({ page: pageParam, per_page: 20, sort: 'name:asc', query: query || undefined }),
    initialPageParam: 1,
    getNextPageParam: (lastPage: ApiResponse<FeatureFlag[]>) => {
      if ((lastPage?.meta.pagination?.page ?? 0) < (lastPage?.meta.pagination?.pages ?? 0)) {
        return (lastPage?.meta.pagination?.page ?? 0) + 1;
      }
      return undefined;
    },
  });

  const featureFlags = infiniteFlags?.pages?.flatMap((page) => page.data) ?? [];

  const invalidateFlags = () => {
    queryClient.invalidateQueries({ queryKey: ['manageFeatureFlags'] });
    queryClient.invalidateQueries({ queryKey: ['featureFlags'] });
  };

  const toggleMutation = useMutation({
    mutationKey: ['toggleFeatureFlag'],
    mutationFn: ({ flagId, enabled }: { flagId: number; enabled: boolean }) => toggleFeatureFlag(flagId, { enabled }),
    onSuccess: () => {
      invalidateFlags();
      snackbarData('Feature flag updated successfully', 'success');
    },
    onError: (error) => snackbarData(error?.message || 'Failed to update feature flag. Please try again.', 'error'),
  });

  const accessMutation = useMutation({
    mutationKey: ['updateFeatureFlagAccess'],
    mutationFn: ({ flagId, access }: { flagId: number; access: FeatureFlagAccess }) =>
      updateFeatureFlagAccess(flagId, access),
    onSuccess: () => {
      invalidateFlags();
      snackbarData('Feature flag visibility updated', 'success');
    },
    onError: (error) =>
      snackbarData(error?.message || 'Failed to update feature flag visibility. Please try again.', 'error'),
  });

  const handleToggle = (flag: FeatureFlag, enabled: boolean) => {
    toggleMutation.mutate({ flagId: flag.id, enabled });
  };

  const handleAccessChange = (flag: FeatureFlag, access: FeatureFlagAccess) => {
    accessMutation.mutate({ flagId: flag.id, access });
  };

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  };

  useEffect(() => {
    if (flagsInView && fetchNextPage && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [flagsInView, fetchNextPage, hasNextPage, isFetchingNextPage]);

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '20px', justifyContent: 'space-between' }}>
        <TextField
          onChange={handleSearchChange}
          value={searchText}
          sx={{ width: 360 }}
          aria-label='Search feature flags'
          placeholder='Search...'
          size='small'
          slotProps={{
            input: {
              sx: { borderRadius: '8px', fontSize: '14px' },
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon size={16} />
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ border: '1px solid #cbd5e0', boxShadow: 'none', maxHeight: 440 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Feature flag</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Visibility</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align='right'>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {featureFlags.length > 0 ? (
                featureFlags.map((flag: FeatureFlag) => (
                  <FeatureFlagRow
                    key={flag.id}
                    flag={flag}
                    isLoading={toggleMutation.isPending || accessMutation.isPending}
                    onToggle={handleToggle}
                    onAccessChange={handleAccessChange}
                  />
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} sx={{ textAlign: 'center', padding: 4, color: 'text.secondary' }}>
                    No feature flags found
                  </TableCell>
                </TableRow>
              )}
              <TableRow ref={flagsRef}>
                <TableCell colSpan={4} sx={{ border: 'none', padding: 1 }}>
                  {isFetchingNextPage && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                      <CircularProgress size={24} />
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Stack>
  );
}

export default FeatureFlagsTable;
